import { useEffect } from 'react'
import {
  usePanelsByBendingMomentCapacity,
  usePanelsBySectionModulus,
  useSteelGradeOptions,
} from '@/hooks/api'
import { usePanels } from '@/hooks/api/usePanels'
import { useDebouncing } from './useDebouncing'
import { usePanelSuggestions } from './usePanelSuggestions'
import { useSearchHandlers } from './useSearchHandlers'
import { useSearchState } from './useSearchState'

export const useMainPageSearch = () => {
  const { searchState, setters, refs } = useSearchState()

  // Debouncing
  const { debouncedSectionModulus, debouncedBendingMoment } = useDebouncing(
    searchState.sectionModulus,
    searchState.sectionModulusUnit,
    searchState.bendingMoment,
  )

  // API calls
  const { data: nameSuggestionsData, isLoading: nameSearchLoading } = usePanels(
    searchState.productName.trim().length > 0
      ? {
          name_search: searchState.productName,
          per_page: 10,
          units: searchState.sectionModulusUnit === 'imperial' ? 'i' : 'm',
        }
      : undefined,
  )

  // Section modulus search
  const shouldSearchSectionModulus = (() => {
    const trimmedValue = debouncedSectionModulus.trim()
    if (trimmedValue.length === 0 || Number.isNaN(parseFloat(trimmedValue))) return false
    const minChars = searchState.sectionModulusUnit === 'metric' ? 3 : 2
    return trimmedValue.length >= minChars
  })()

  const { data: modulusSuggestionsData, isLoading: modulusSearchLoading } =
    usePanelsBySectionModulus(
      shouldSearchSectionModulus
        ? {
            sm: parseFloat(debouncedSectionModulus),
            units: searchState.sectionModulusUnit === 'imperial' ? 'i' : 'm',
            per_page: 50,
          }
        : undefined,
    )

  // Bending moment search
  const shouldSearchBendingMoment = (() => {
    const trimmedValue = debouncedBendingMoment.trim()
    if (trimmedValue.length === 0 || Number.isNaN(parseFloat(trimmedValue))) return false
    return trimmedValue.length >= 3
  })()

  const { data: bendingSuggestionsData, isLoading: bendingSearchLoading } =
    usePanelsByBendingMomentCapacity(
      shouldSearchBendingMoment
        ? {
            bmc: parseFloat(debouncedBendingMoment),
            steel_grade: searchState.selectedSteelGrade,
            units: searchState.bendingMomentUnit === 'imperial' ? 'i' : 'm',
            per_page: 50,
          }
        : undefined,
    )

  // Steel grades
  const { data: steelGradeOptions, isLoading: steelGradesLoading } = useSteelGradeOptions()

  // Process suggestions
  const suggestions =
    !searchState.productName.trim() || !nameSuggestionsData?.data ? [] : nameSuggestionsData.data

  const { modulusSuggestions, bendingSuggestions } = usePanelSuggestions(
    debouncedSectionModulus,
    modulusSuggestionsData,
    debouncedBendingMoment,
    bendingSuggestionsData,
  )

  // Handlers
  const handlers = useSearchHandlers(
    searchState,
    {
      setProductName: setters.setProductName,
      setShowSuggestions: setters.setShowSuggestions,
      setSelectedIndex: setters.setSelectedIndex,
      setSearchError: setters.setSearchError,
    },
    suggestions,
    modulusSuggestions,
    bendingSuggestions,
  )

  // Handle click outside to close suggestions
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        refs.searchContainerRef.current &&
        !refs.searchContainerRef.current.contains(event.target as Node)
      ) {
        setters.setShowSuggestions(false)
      }
      if (
        refs.modulusSearchRef.current &&
        !refs.modulusSearchRef.current.contains(event.target as Node)
      ) {
        setters.setShowModulusSuggestions(false)
      }
      if (
        refs.bendingSearchRef.current &&
        !refs.bendingSearchRef.current.contains(event.target as Node)
      ) {
        setters.setShowBendingSuggestions(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [refs, setters])

  return {
    searchState,
    setters,
    refs,
    suggestions,
    modulusSuggestions,
    bendingSuggestions,
    handlers,
    loading: {
      nameSearchLoading,
      modulusSearchLoading,
      bendingSearchLoading,
      steelGradesLoading,
    },
    steelGradeOptions,
  }
}
